// ============================================================
// TERRAIN ARRIVALS - when each face of the map resolves.
//
// The map had one animation and one switch. Getting that one visible
// took three attempts (v7.97.0 retimed it; v7.98.0 found it drawing its
// wireframe in the face's own colour, and `prefers-reduced-motion`
// killing it silently while the button still read ON), and once it
// worked, one effect was a thin return on a mechanism that carries many.
//
// FIVE MOTIONS, EACH WITH ITS OWN ORDER. They began as five orderings
// over one shared effect, and the honest verdict was "they are
// indistinguishable to me - Rise doesn't rise anything, it looks like
// Sweep". Varying only WHEN a face resolves is a distinction you have to
// be told about to see, so each varies HOW as well (v7.100.0).
//
// This module still owns only the WHEN. The how lives in the stylesheet,
// one class per entry.
//
// Pure. Zero React dependency.
// ============================================================
import type { SceneFace } from './scene';

export type TerrainAnimation = 'none' | 'sweep' | 'rise' | 'bloom' | 'approach' | 'scatter';

export interface TerrainAnimationEntry {
  key: TerrainAnimation;
  /** What the selector calls it. */
  label: string;
  /** What it looks like, for the tooltip. */
  hint: string;
}

/**
 * The catalogue, in the order the selector offers them.
 *
 * NONE FIRST, because it is the default. The map is a working surface
 * before it is a spectacle, and a GM loading terrain mid-fight should
 * not have to wait on an effect they did not ask for.
 */
export const TERRAIN_ANIMATIONS: TerrainAnimationEntry[] = [
  { key: 'none', label: 'No animation', hint: 'Terrain simply appears' },
  { key: 'sweep', label: 'Sweep', hint: 'A line passes across the battlefield' },
  { key: 'rise', label: 'Rise', hint: 'The map builds upward, from the ground' },
  { key: 'bloom', label: 'Bloom', hint: 'Terrain spreads outward from the middle' },
  { key: 'approach', label: 'Approach', hint: 'The map arrives from the horizon' },
  { key: 'scatter', label: 'Scatter', hint: 'A signal resolving out of noise' },
];

const KEYS = new Set<string>(TERRAIN_ANIMATIONS.map((entry) => entry.key));

/** Is this what a stored preference claims it is? */
export function isTerrainAnimation(value: unknown): value is TerrainAnimation {
  return typeof value === 'string' && KEYS.has(value);
}

/** How long one face takes to resolve. MUST match the CSS duration. */
export const FACE_MS = 380;

/**
 * The longest the ordering ever runs, end to end.
 *
 * MUCH longer than one face's resolve, and that ratio is the point. At
 * 1100 against 760 the last face began before the first had finished, so
 * every face was mid-animation at once and the wave was smeared into a
 * shimmer - an ordering nobody could perceive is not an ordering
 * (v7.100.0).
 */
export const MAX_SPREAD_MS = 2400;

/**
 * How much terrain has to be arriving before the spread is at full
 * length. Roughly four cubes' worth of visible faces.
 */
export const FULL_SPREAD_FACES = 24;

/**
 * A NOTE ON THE RATIO. `FACE_MS` and `MAX_SPREAD_MS` are not
 * independent: the wave only reads as a wave while the spread is several
 * times one face's resolve. Change one and check the other.
 */

/**
 * THE SPREAD IS AS LONG AS THERE IS TERRAIN TO SPREAD OVER.
 *
 * This is what lets the same setting serve a map load and a brush
 * stroke. Every ordering below derives a face's turn from WHERE it is,
 * so a fixed spread would leave a single cube laid at the far edge
 * sitting invisible for a second - the brush would feel broken.
 *
 * Scaling by how much is arriving removes the problem rather than
 * special-casing it: six faces resolve in a couple of hundred
 * milliseconds because there is nothing else in their batch to wait for,
 * and a whole map still takes the full sweep. Nothing has to know
 * whether terrain came from a preset, the library, another GM's client,
 * or a brush.
 */
export function spreadFor(arriving: number): number {
  if (arriving <= 0) return 0;
  return Math.round(MAX_SPREAD_MS * Math.min(1, arriving / FULL_SPREAD_FACES));
}

/**
 * A face's own number in [0, 1), stable for the life of the map.
 *
 * FNV-1a over the key. NOT `Math.random()`: E2E replaces the dice source
 * with a seeded sequence, and a stray consumer silently shifts every
 * later roll (gotchas, v6.45.0). It also has to be stable - a face that
 * drew a different number on each rebuild would re-animate, and the
 * whole map would shimmer whenever anything changed.
 */
export function scatterOf(key: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    // The FNV prime, as shifts - `Math.imul` keeps it in 32 bits.
    hash = Math.imul(hash, 0x01000193);
  }
  // Mix the high bits down; the low bits of FNV alone clump badly for
  // keys as similar as "sol-7:0" and "sol-7:1".
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x2545f491);
  hash ^= hash >>> 13;
  return (hash >>> 0) / 4294967296;
}

interface Viewport {
  width: number;
  height: number;
}

/**
 * What an arrival reads of a face: its key, where its solid stands on screen, its height and its depth - a projected
 * `SceneFace` on the svg, or the GPU map's record of one at the moment it appears (gpu/arrivals).
 */
export type ArrivingFace = Pick<SceneFace, 'key' | 'anchor' | 'elevation' | 'depth'>;

/** What each ordering reads off a face. Larger sorts LATER. */
const ORDER_BY: Record<Exclude<TerrainAnimation, 'none'>, (face: ArrivingFace, view: Viewport) => number> = {
  // Across the screen, from where each solid STANDS rather than from
  // where its face happens to be drawn - a wall's side face is metres up,
  // and ordering by that would break the line into steps.
  sweep: (face) => face.anchor.x,
  // Up. The one that shows what the map gained in v7.97.0.
  rise: (face) => face.elevation,
  // Outward from the middle of the view.
  bloom: (face, view) => Math.hypot(face.anchor.x - view.width / 2, face.anchor.y - view.height / 2),
  // `depth` is distance FROM the viewer, so the far side has the larger
  // number and must go first - hence the negation.
  approach: (face) => -face.depth,
  scatter: (face) => scatterOf(face.key),
};

/**
 * When each arriving face resolves, in milliseconds from now.
 *
 * NORMALISED OVER THE ARRIVING SET'S OWN RANGE, not over the world's, so
 * a corner of a small map and a whole large one both use the full
 * spread. Something always goes first at 0 and something always goes
 * last.
 *
 * Returns an empty map for `none` and for any kind this version does not
 * know - a preference written by a later build should degrade to no
 * animation rather than to an exception.
 */
export function arrivalDelays(arriving: ArrivingFace[], kind: TerrainAnimation, view: Viewport): Map<string, number> {
  const out = new Map<string, number>();
  const order = kind === 'none' ? undefined : ORDER_BY[kind as Exclude<TerrainAnimation, 'none'>];
  if (!order || !arriving.length) return out;

  const spread = spreadFor(arriving.length);
  const values = arriving.map((face) => order(face, view));
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low;

  arriving.forEach((face, i) => {
    // Everything at the same position arrives together - and a single
    // face has nothing to be ordered against, so it waits for nothing.
    const fraction = span > 1e-9 ? (values[i] - low) / span : 0;
    out.set(face.key, Math.round(fraction * spread));
  });
  return out;
}
